/**
 * Thẻ hiển thị BĐS trên grid hoặc hàng ngang (listing row).
 *
 * Hai layout:
 * - grid (mặc định): title + location + price + meta specs.
 * - row: headline gộp category+location, giá muted, badge hot, lượt xem.
 *
 * Giá công khai dùng formatPropertyPrice (có mask).
 */
import { Link } from 'react-router-dom';
import { FavoriteButton } from './FavoriteButton';
import { PropertyCardMedia } from './PropertyCardMedia';
import { APP_ROUTES } from '../../shared/constants/routes';
import { TRANSACTION_TYPES } from '../../shared/constants/property';
import { TEXT } from '../../shared/languages';
import { getCategoryName } from '../../shared/utils/categoryLabel';
import { formatCompactCount } from '../../shared/utils/formatCompactCount';
import { formatPropertyPrice } from '../../shared/utils/formatCurrency';

/**
 * Card BĐS dùng trên trang chủ, tìm kiếm, yêu thích.
 * @param {Object} props
 * @param {Object} props.property - Dữ liệu BĐS từ API
 * @param {'grid'|'row'} [props.layout='grid'] - Kiểu bố cục
 */
export const PropertyCard = ({ property, layout = 'grid' }) => {
  const common = TEXT.common;
  const categoryName = getCategoryName(property.category) || TEXT.property.defaultCategory;
  const locationLabel = property.district || property.location || property.province || '';
  const isRowLayout = layout === 'row';
  const viewCount = property.views_count ?? property.views ?? 0;
  const isHot = property.badges?.is_hot;

  // Ghép các thông số phòng/diện tích thành chuỗi meta
  const metaParts = [];

  if (property.bedrooms !== null && property.bedrooms !== undefined) {
    metaParts.push(`${property.bedrooms} ${common.bedroomShort}`);
  }

  if (property.bathrooms !== null && property.bathrooms !== undefined) {
    metaParts.push(`${property.bathrooms} ${common.bathroomShort}`);
  }

  if (property.toilets !== null && property.toilets !== undefined) {
    metaParts.push(`${property.toilets} toilet`);
  }

  if (property.kitchens !== null && property.kitchens !== undefined) {
    metaParts.push(`${property.kitchens} bếp`);
  }

  if (property.area) {
    metaParts.push(`${property.area} ${common.areaUnit}`);
  }

  const headline = locationLabel ? `${categoryName} tại ${locationLabel}` : categoryName;
  const priceLabel =
    property.transaction_type === TRANSACTION_TYPES.rent
      ? `${formatPropertyPrice(property.price)} / tháng`
      : formatPropertyPrice(property.price);

  return (
    <article className={`property-card ${isRowLayout ? 'property-card--row' : ''}`}>
      <div className="property-image-wrap">
        <PropertyCardMedia
          propertyId={property.id}
          title={property.title}
          images={property.images}
          detailPath={APP_ROUTES.propertyDetails(property.id)}
          showBadge={isRowLayout && isHot}
          badgeLabel={TEXT.badges.hot}
        />
        <FavoriteButton propertyId={property.id} variant="overlay" />
      </div>
      <div className="property-body">
        <div className="property-card-head">
          {isRowLayout ? (
            <>
              <h3>
                <Link to={APP_ROUTES.propertyDetails(property.id)}>{headline}</Link>
              </h3>
              <div className="property-card-subline">
                <span className="property-price-muted">{priceLabel}</span>
                {viewCount > 0 && (
                  <span className="property-rating" aria-label={`${viewCount} lượt xem`}>
                    <svg viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
                      <path d="M16 2.5l4.1 8.3 9.2 1.3-6.7 6.5 1.6 9.2L16 23.8l-8.2 4.3 1.6-9.2-6.7-6.5 9.2-1.3L16 2.5z" />
                    </svg>
                    {formatCompactCount(viewCount)}
                  </span>
                )}
              </div>
            </>
          ) : (
            <>
              <h3>
                <Link to={APP_ROUTES.propertyDetails(property.id)}>{property.title}</Link>
              </h3>
              <p className="property-location">
                {locationLabel ? `${categoryName} tại ${locationLabel}` : categoryName}
              </p>
            </>
          )}
        </div>
        {!isRowLayout && (
          <p className="property-price-line">
            <strong>{priceLabel}</strong>
            {metaParts.length > 0 && <span> · {metaParts.join(' · ')}</span>}
          </p>
        )}
      </div>
    </article>
  );
};
